import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import {
  ChartLineUp, Users, Stack, MagnifyingGlass, FileArrowDown, CreditCard,
  LockKey, ArrowClockwise, SignOut, Fire, EnvelopeSimple, LinkSimple, Copy, Check,
  Trash, Eye, Warning,
} from "@phosphor-icons/react";

const KEY_STORE = "lc_admin_key";
const PROD_ORIGIN = "https://getlcshield.com";
const LINK_LANGS = [
  { code: "en", label: "EN" },
  { code: "zh-TW", label: "繁" },
  { code: "zh-CN", label: "简" },
];

function timeAgo(iso) {
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtStamp(iso) {
  try {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch { return iso || "—"; }
}

const EVENT_LABEL = {
  demo_scenario_loaded: "loaded sample scenario",
  discrepancy_check_run: "ran a discrepancy check",
  report_exported: "exported a report",
  paywall_modal_opened: "opened the paywall",
  stripe_checkout_initiated: "started Stripe checkout",
  try_sample_clicked: "clicked Try Sample",
  user_registered: "entered email / unlock",
  checkout_initiated: "started checkout",
  payment_completed: "completed payment",
};

const ACTION_LABEL = {
  try_sample_clicked: "Tried Sample",
  user_registered: "Registered / Unlock",
  checkout_initiated: "Started Checkout",
  stripe_checkout_initiated: "Started Checkout",
  paywall_modal_opened: "Opened Paywall",
  payment_completed: "Payment Completed",
};

const HOT_EVENTS = new Set(["payment_completed", "checkout_initiated", "stripe_checkout_initiated"]);

const ACTIVITY_TABS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid Users" },
  { key: "registered", label: "Registered" },
  { key: "sample", label: "Try Sample Clickers" },
  { key: "cold", label: "Cold Email Leads" },
];

function matchesTab(r, tab) {
  switch (tab) {
    case "paid": return r.is_paid || r.event === "payment_completed";
    case "registered": return r.event === "user_registered";
    case "sample": return r.event === "try_sample_clicked";
    case "cold": return r.from_cold_email;
    default: return true;
  }
}

const StatCard = ({ icon: Icon, label, value, testid, breakdown }) => (
  <div className="rounded-md border border-white/10 bg-lc-slate/50 p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-lc-fog">
      <Icon size={18} weight="duotone" className="text-lc-sky" />
      <span className="text-xs uppercase tracking-widest">{label}</span>
    </div>
    <div className="mt-3 font-serif text-4xl text-white">{value}</div>
    {breakdown && Object.keys(breakdown).length > 0 && (
      <div className="mt-3 flex flex-wrap gap-1.5">
        {Object.entries(breakdown).map(([k, v]) => (
          <span key={k} className="rounded-sm bg-white/5 px-2 py-0.5 font-mono text-[11px] text-lc-fog">
            {k}: <span className="text-lc-sky">{v}</span>
          </span>
        ))}
      </div>
    )}
  </div>
);

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem(KEY_STORE) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [recent, setRecent] = useState([]);
  const [activity, setActivity] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [sources, setSources] = useState([]);
  const [view, setView] = useState("overview");
  const [tab, setTab] = useState("all");
  const [linkEmail, setLinkEmail] = useState("");
  const [linkLang, setLinkLang] = useState("en");
  const [copied, setCopied] = useState(false);
  const [bulkCopied, setBulkCopied] = useState(false);
  // ---- data cleanup panel ----
  const [cleanMode, setCleanMode] = useState("delete");
  const [cleanDates, setCleanDates] = useState("");
  const [cleanSessions, setCleanSessions] = useState("");
  const [cleanCities, setCleanCities] = useState("");
  const [cleanCountries, setCleanCountries] = useState("");
  const [cleanTx, setCleanTx] = useState(false);
  const [cleanPreview, setCleanPreview] = useState(null);
  const [cleanBusy, setCleanBusy] = useState(false);

  const load = useCallback(async (passkey) => {
    setLoading(true);
    setError("");
    try {
      const headers = { "x-admin-passkey": passkey };
      const [m, r, a, f, s] = await Promise.all([
        api.get("/admin/metrics", { headers }),
        api.get("/admin/recent", { headers }),
        api.get("/admin/activity", { headers }),
        api.get("/admin/funnel", { headers }),
        api.get("/admin/sources", { headers }),
      ]);
      setMetrics(m.data);
      setRecent(r.data.events || []);
      setActivity(a.data.rows || []);
      setFunnel(f.data.rows || []);
      setSources(s.data.rows || []);
      setAuthed(true);
      localStorage.setItem(KEY_STORE, passkey);
      setKey(passkey);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Invalid admin passkey.");
        setAuthed(false);
        localStorage.removeItem(KEY_STORE);
      } else {
        setError("Could not load metrics. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem(KEY_STORE);
    setKey("");
    setAuthed(false);
    setMetrics(null);
    setRecent([]);
    setActivity([]);
    setFunnel([]);
    setSources([]);
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const buildLink = (em) => {
    const params = new URLSearchParams();
    params.set("ref_email", em.trim().toLowerCase());
    if (linkLang && linkLang !== "en") params.set("lang", linkLang);
    return `${PROD_ORIGIN}/?${params.toString()}`;
  };

  // Parse the input into a de-duplicated list of valid emails (line/comma/space separated).
  const parsedEmails = () => {
    const seen = new Set();
    const out = [];
    (linkEmail || "").split(/[\n,;\s]+/).forEach((raw) => {
      const em = raw.trim().toLowerCase();
      if (em && EMAIL_RE.test(em) && !seen.has(em)) { seen.add(em); out.push(em); }
    });
    return out;
  };

  const copyLink = async () => {
    const emails = parsedEmails();
    if (emails.length !== 1) return;
    try {
      await navigator.clipboard.writeText(buildLink(emails[0]));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked — preview still shows the link */ }
  };

  const copyAllLinks = async () => {
    const emails = parsedEmails();
    if (emails.length === 0) return;
    const text = emails.map((em) => buildLink(em)).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setBulkCopied(true);
      setTimeout(() => setBulkCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  const exportLinksCSV = () => {
    const emails = parsedEmails();
    if (emails.length === 0) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Email", "Tracking Link", "Language"].map(esc).join(",")];
    emails.forEach((em) => lines.push([em, buildLink(em), linkLang].map(esc).join(",")));
    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const p = (n) => String(n).padStart(2, "0");
    const d = new Date();
    a.href = url;
    a.download = `lc-tracking-links-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportActivityCSV = () => {
    const rows = activity.filter((r) => matchesTab(r, tab));
    if (rows.length === 0) return;
    const header = ["Timestamp", "Customer/Identifier", "Action", "Plan/Status", "Intent", "Location", "Engine", "From Cold Email"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(esc).join(",")];
    rows.forEach((r) => {
      const hot = r.is_paid || HOT_EVENTS.has(r.event);
      const planStatus = r.is_paid ? `Paid${r.plan ? ` · ${r.plan}` : ""}` : (r.plan || "");
      lines.push([
        r.ts,
        r.user_email || `anon · ${(r.session_id || "").slice(0, 8)}`,
        ACTION_LABEL[r.event] || r.event,
        planStatus,
        hot ? "Hot Client" : "Warm Lead",
        r.geo || "",
        r.engine || "",
        r.from_cold_email ? "yes" : "no",
      ].map(esc).join(","));
    });
    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const p = (n) => String(n).padStart(2, "0");
    const d = new Date();
    a.href = url;
    a.download = `lc-high-intent-${tab}-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- data cleanup ----
  const cleanPayload = () => {
    const split = (s) => (s || "").split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
    return {
      mode: cleanMode,
      dates: split(cleanDates),
      session_ids: split(cleanSessions),
      cities: split(cleanCities),
      countries: split(cleanCountries),
      include_transactions: cleanTx,
    };
  };
  const runCleanPreview = async () => {
    setCleanBusy(true);
    try {
      const r = await api.post("/admin/cleanup/preview", cleanPayload(), { headers: { "x-admin-passkey": key } });
      setCleanPreview(r.data);
    } catch (e) {
      setCleanPreview(null);
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally { setCleanBusy(false); }
  };
  const runCleanExecute = async () => {
    if (!cleanPreview || cleanPreview.events_matched === 0) return;
    const txNote = cleanTx && cleanPreview.transactions_matched ? ` and ${cleanPreview.transactions_matched} transaction(s)` : "";
    if (!window.confirm(`Permanently delete ${cleanPreview.events_matched} event(s)${txNote}? This cannot be undone.`)) return;
    setCleanBusy(true);
    try {
      const r = await api.post("/admin/cleanup/execute", cleanPayload(), { headers: { "x-admin-passkey": key } });
      setCleanPreview(null);
      setCleanDates(""); setCleanSessions(""); setCleanCities(""); setCleanCountries("");
      await load(key);
      toast.success(`Deleted ${r.data.deleted_events} event(s)${r.data.deleted_transactions ? `, ${r.data.deleted_transactions} txn(s)` : ""}. ${r.data.events_remaining} remain.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally { setCleanBusy(false); }
  };
  if (!authed) {
    return (
      <div className="min-h-screen bg-lc-navy text-white flex items-center justify-center px-4">
        <Seo title="Admin · LC Shield" noindex />
        <form
          onSubmit={(e) => { e.preventDefault(); if (input.trim()) load(input.trim()); }}
          className="w-full max-w-sm rounded-md border border-white/10 bg-lc-slate/50 p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2 text-lc-sky">
            <LockKey size={22} weight="duotone" />
            <h1 className="font-serif text-2xl text-white">Admin Access</h1>
          </div>
          <p className="mt-2 text-sm text-lc-fog">Enter the admin passkey to view analytics.</p>
          <input
            type="password"
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Admin passkey"
            data-testid="admin-passkey-input"
            className="mt-5 w-full rounded-sm border border-white/15 bg-lc-navy px-3 py-2.5 text-sm text-white placeholder:text-lc-steel focus:border-lc-sky focus:outline-none"
          />
          {error && <p className="mt-3 text-sm text-lc-critical" data-testid="admin-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-5 w-full rounded-sm bg-lc-sky px-4 py-2.5 text-sm font-semibold text-lc-navy transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Verifying…" : "Unlock Dashboard"}
          </button>
        </form>
      </div>
    );
  }

  const t = metrics?.totals || {};
  return (
    <div className="min-h-screen bg-lc-navy text-white" data-testid="admin-dashboard">
      <Seo title="Admin · LC Shield" noindex />
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChartLineUp size={26} weight="duotone" className="text-lc-sky" />
            <h1 className="font-serif text-3xl">LC Shield · Admin</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="mr-1 flex items-center rounded-sm border border-white/15 p-0.5" data-testid="admin-view-switcher">
              {[{ k: "overview", label: "Overview" }, { k: "funnel", label: "Campaign Funnel" }].map((v) => (
                <button key={v.k} onClick={() => setView(v.k)} data-testid={`admin-view-${v.k}`}
                  className={`rounded-sm px-3 py-1.5 text-[11px] font-semibold transition-colors duration-200 ${
                    view === v.k ? "bg-lc-sky text-lc-navy" : "text-lc-fog hover:text-white"
                  }`}>
                  {v.label}
                </button>
              ))}
            </div>
            <button onClick={() => load(key)} data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-1.5 rounded-sm border border-white/15 px-3 py-1.5 text-xs font-semibold text-lc-fog hover:bg-white/5 transition-colors duration-200">
              <ArrowClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 rounded-sm border border-white/15 px-3 py-1.5 text-xs font-semibold text-lc-fog hover:bg-white/5 transition-colors duration-200">
              <SignOut size={14} weight="bold" /> Logout
            </button>
          </div>
        </div>

        {view === "funnel" ? (
          <>
          <div className="mt-8 rounded-md border border-white/10 bg-lc-slate/50" data-testid="campaign-funnel">
            <div className="border-b border-white/10 px-5 py-4">
              <h2 className="flex items-center gap-2 font-serif text-lg">
                <ChartLineUp size={18} weight="duotone" className="text-lc-sky" /> Campaign Funnel
              </h2>
              <p className="mt-1 text-xs text-lc-fog">Cold-email leads grouped by company domain. Clicked → Engaged (Try Sample / check run) → Converted (paid).</p>
            </div>
            {funnel.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-lc-steel" data-testid="funnel-empty">No cold-email campaigns tracked yet. Generate tracking links below and share them to start attributing.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm" data-testid="funnel-table">
                  <thead>
                    <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-lc-steel">
                      <th className="px-5 py-2.5 font-medium">Campaign (Domain)</th>
                      <th className="px-5 py-2.5 font-medium">Clicked Link</th>
                      <th className="px-5 py-2.5 font-medium">Workspace Engaged</th>
                      <th className="px-5 py-2.5 font-medium">Converted (Paid)</th>
                      <th className="px-5 py-2.5 font-medium">Click→Engage</th>
                      <th className="px-5 py-2.5 font-medium">Engage→Convert</th>
                      <th className="px-5 py-2.5 font-medium">Overall</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {funnel.map((r, i) => (
                      <tr key={i} className="text-lc-fog hover:bg-white/5 transition-colors duration-150" data-testid={`funnel-row-${i}`}>
                        <td className="px-5 py-2.5 font-medium text-white">{r.campaign}</td>
                        <td className="px-5 py-2.5">{r.clicked}</td>
                        <td className="px-5 py-2.5">{r.engaged}</td>
                        <td className="px-5 py-2.5 text-lc-sky">{r.converted}</td>
                        <td className="px-5 py-2.5 font-mono text-[12px]">{r.rate_engage}%</td>
                        <td className="px-5 py-2.5 font-mono text-[12px]">{r.rate_convert}%</td>
                        <td className="px-5 py-2.5 font-mono text-[12px] text-white">{r.rate_overall}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Cold-email campaign sources (?demo=sample&src=) */}
          <div className="mt-6 rounded-md border border-white/10 bg-lc-slate/50" data-testid="campaign-sources">
            <div className="border-b border-white/10 px-5 py-4">
              <h2 className="flex items-center gap-2 font-serif text-lg">
                <LinkSimple size={18} weight="duotone" className="text-lc-sky" /> Sample Deep-Link Sources
              </h2>
              <p className="mt-1 text-xs text-lc-fog">Traffic from <span className="font-mono">?demo=sample&amp;src=</span> cold-outreach links. Opens = launches of the auto sample audit.</p>
            </div>
            {sources.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-lc-steel" data-testid="sources-empty">No sample deep-link traffic yet. Share links like <span className="font-mono">/?demo=sample&amp;src=cold_email_v1</span> to start attributing.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="sources-table">
                  <thead>
                    <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-lc-steel">
                      <th className="px-5 py-2.5 font-medium">Source (src)</th>
                      <th className="px-5 py-2.5 font-medium">Sample Opens</th>
                      <th className="px-5 py-2.5 font-medium">Unique Sessions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {sources.map((r, i) => (
                      <tr key={i} className="text-lc-fog hover:bg-white/5 transition-colors duration-150" data-testid={`sources-row-${i}`}>
                        <td className="px-5 py-2.5 font-medium text-white">{r.src}</td>
                        <td className="px-5 py-2.5 text-lc-sky">{r.opens}</td>
                        <td className="px-5 py-2.5">{r.sessions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
        ) : (
        <>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard icon={Users} label="Site Sessions" value={metrics?.sessions ?? 0} testid="stat-sessions" />
          <StatCard icon={Stack} label="Scenarios Loaded" value={t.demo_scenario_loaded ?? 0} breakdown={metrics?.scenarios} testid="stat-scenarios" />
          <StatCard icon={MagnifyingGlass} label="Checks Run" value={t.discrepancy_check_run ?? 0} breakdown={metrics?.checks} testid="stat-checks" />
          <StatCard icon={FileArrowDown} label="Report Exports" value={t.report_exported ?? 0} breakdown={metrics?.exports} testid="stat-exports" />
          <StatCard icon={CreditCard} label="Paywall Clicks" value={t.paywall_modal_opened ?? 0} breakdown={metrics?.paywall} testid="stat-paywall" />
          <StatCard icon={CreditCard} label="Checkouts / Active Subs"
            value={`${t.stripe_checkout_initiated ?? 0} / ${metrics?.active_subscriptions ?? 0}`} testid="stat-subs" />
        </div>

        {/* ---- Data Cleanup (purge internal/test analytics; counters resync live) ---- */}
        <div className="mt-8 rounded-md border border-white/10 bg-lc-slate/50 p-5" data-testid="data-cleanup">
          <h2 className="flex items-center gap-2 font-serif text-lg">
            <Trash size={18} weight="duotone" className="text-lc-sky" /> Data Cleanup
          </h2>
          <p className="mt-1.5 text-xs text-lc-fog">
            Purge internal / test activity so counters reflect real visitors only. Always <strong>Preview</strong> first —
            deletion is permanent and all dashboard tiles recompute on the next load.
          </p>

          <div className="mt-4 flex items-center rounded-sm border border-white/15 p-0.5" data-testid="cleanup-mode-switcher">
            {[{ k: "delete", label: "Delete matching" }, { k: "retain", label: "Keep only these sessions" }].map((m) => (
              <button key={m.k} onClick={() => { setCleanMode(m.k); setCleanPreview(null); }} data-testid={`cleanup-mode-${m.k}`}
                className={`rounded-sm px-3 py-1.5 text-[11px] font-semibold transition-colors duration-200 ${
                  cleanMode === m.k ? "bg-lc-sky text-lc-navy" : "text-lc-fog hover:text-white"
                }`}>
                {m.label}
              </button>
            ))}
          </div>

          {cleanMode === "retain" && (
            <p className="mt-3 flex items-center gap-1.5 rounded-sm border border-lc-warn/30 bg-lc-warn/10 px-3 py-2 text-[11px] text-lc-warn" data-testid="cleanup-retain-note">
              <Warning size={13} weight="fill" /> Retain mode keeps ONLY events whose session id matches the list below — everything else is deleted.
            </p>
          )}

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-wider text-lc-steel">Session IDs (8-char or full)</span>
              <textarea value={cleanSessions} onChange={(e) => { setCleanSessions(e.target.value); setCleanPreview(null); }} rows={2}
                placeholder={"3a997886\n2cd645ce"} data-testid="cleanup-sessions"
                className="mt-1 w-full resize-y rounded-sm border border-white/15 bg-lc-navy px-3 py-2 font-mono text-[12px] text-white placeholder:text-lc-steel focus:border-lc-sky focus:outline-none" />
            </label>
            <label className={`block ${cleanMode === "retain" ? "opacity-40" : ""}`}>
              <span className="font-mono text-[10px] uppercase tracking-wider text-lc-steel">Dates (YYYY-MM-DD)</span>
              <textarea value={cleanDates} onChange={(e) => { setCleanDates(e.target.value); setCleanPreview(null); }} rows={2} disabled={cleanMode === "retain"}
                placeholder={"2026-09-22\n2026-09-11"} data-testid="cleanup-dates"
                className="mt-1 w-full resize-y rounded-sm border border-white/15 bg-lc-navy px-3 py-2 font-mono text-[12px] text-white placeholder:text-lc-steel focus:border-lc-sky focus:outline-none disabled:cursor-not-allowed" />
            </label>
            <label className={`block ${cleanMode === "retain" ? "opacity-40" : ""}`}>
              <span className="font-mono text-[10px] uppercase tracking-wider text-lc-steel">Cities (contains)</span>
              <textarea value={cleanCities} onChange={(e) => { setCleanCities(e.target.value); setCleanPreview(null); }} rows={2} disabled={cleanMode === "retain"}
                placeholder={"Taipei"} data-testid="cleanup-cities"
                className="mt-1 w-full resize-y rounded-sm border border-white/15 bg-lc-navy px-3 py-2 font-mono text-[12px] text-white placeholder:text-lc-steel focus:border-lc-sky focus:outline-none disabled:cursor-not-allowed" />
            </label>
            <label className={`block ${cleanMode === "retain" ? "opacity-40" : ""}`}>
              <span className="font-mono text-[10px] uppercase tracking-wider text-lc-steel">Countries (contains)</span>
              <textarea value={cleanCountries} onChange={(e) => { setCleanCountries(e.target.value); setCleanPreview(null); }} rows={2} disabled={cleanMode === "retain"}
                placeholder={"Taiwan"} data-testid="cleanup-countries"
                className="mt-1 w-full resize-y rounded-sm border border-white/15 bg-lc-navy px-3 py-2 font-mono text-[12px] text-white placeholder:text-lc-steel focus:border-lc-sky focus:outline-none disabled:cursor-not-allowed" />
            </label>
          </div>

          {cleanMode === "delete" && (
            <label className="mt-3 flex items-center gap-2 text-xs text-lc-fog" data-testid="cleanup-tx-label">
              <input type="checkbox" checked={cleanTx} onChange={(e) => { setCleanTx(e.target.checked); setCleanPreview(null); }} data-testid="cleanup-include-tx" />
              Also purge matching Stripe transactions (by date / session)
            </label>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button onClick={runCleanPreview} disabled={cleanBusy} data-testid="cleanup-preview-btn"
              className="inline-flex items-center gap-1.5 rounded-sm border border-lc-sky/40 bg-lc-sky/10 px-4 py-2 text-sm font-semibold text-lc-sky hover:bg-lc-sky/20 transition-colors duration-200 disabled:opacity-50">
              <Eye size={15} weight="bold" /> {cleanBusy ? "Working…" : "Preview"}
            </button>
            <button onClick={runCleanExecute} disabled={cleanBusy || !cleanPreview || cleanPreview.events_matched === 0} data-testid="cleanup-execute-btn"
              className="inline-flex items-center gap-1.5 rounded-sm bg-lc-critical px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity duration-200 disabled:opacity-40">
              <Trash size={15} weight="bold" /> Delete permanently
            </button>
          </div>

          {cleanPreview && (
            <div className="mt-4 rounded-sm border border-white/10 bg-lc-navy p-4" data-testid="cleanup-preview-result">
              <div className="flex flex-wrap gap-4 font-mono text-[12px]">
                <span className="text-lc-fog">Matched to delete: <span className="text-lc-critical" data-testid="cleanup-matched">{cleanPreview.events_matched}</span></span>
                <span className="text-lc-fog">Total events: <span className="text-white">{cleanPreview.events_total}</span></span>
                <span className="text-lc-fog">Remaining after: <span className="text-lc-sky" data-testid="cleanup-remaining">{cleanPreview.events_remaining}</span></span>
                {typeof cleanPreview.transactions_matched === "number" && (
                  <span className="text-lc-fog">Txns to delete: <span className="text-lc-critical">{cleanPreview.transactions_matched}</span></span>
                )}
              </div>
              {cleanPreview.events_matched === 0 ? (
                <p className="mt-3 text-xs text-lc-steel" data-testid="cleanup-no-match">No events match these criteria — nothing to delete.</p>
              ) : (
                <div className="mt-3 max-h-52 overflow-y-auto">
                  <table className="w-full text-left text-[11px]" data-testid="cleanup-sample-table">
                    <thead>
                      <tr className="font-mono text-[9px] uppercase tracking-wider text-lc-steel">
                        <th className="py-1 pr-3 font-medium">Timestamp</th>
                        <th className="py-1 pr-3 font-medium">Session</th>
                        <th className="py-1 pr-3 font-medium">Event</th>
                        <th className="py-1 pr-3 font-medium">Location</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {cleanPreview.sample.map((s, i) => (
                        <tr key={i} className="text-lc-fog">
                          <td className="py-1 pr-3 font-mono text-lc-steel">{fmtStamp(s.ts)}</td>
                          <td className="py-1 pr-3 font-mono">anon · {s.session_id}</td>
                          <td className="py-1 pr-3">{s.event}</td>
                          <td className="py-1 pr-3">{s.geo || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-[10px] text-lc-steel">Showing up to 20 of {cleanPreview.events_matched} matched events.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-8 rounded-md border border-white/10 bg-lc-slate/50 p-5" data-testid="link-generator">
          <h2 className="flex items-center gap-2 font-serif text-lg">
            <LinkSimple size={18} weight="duotone" className="text-lc-sky" /> Cold Email Link Generator
          </h2>
          <p className="mt-1.5 text-xs text-lc-fog">Paste one email — or many, one per line — to build tracking links. Every click and action lights up in the log, attributed to each prospect.</p>
          {(() => {
            const emails = parsedEmails();
            const isBulk = emails.length > 1;
            return (
              <>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start">
                  <textarea
                    value={linkEmail}
                    onChange={(e) => setLinkEmail(e.target.value)}
                    rows={3}
                    placeholder={"prospect@company.com\nbuyer@another-co.hk\n…"}
                    data-testid="link-email-input"
                    className="w-full flex-1 resize-y rounded-sm border border-white/15 bg-lc-navy px-3 py-2.5 text-sm text-white placeholder:text-lc-steel focus:border-lc-sky focus:outline-none"
                  />
                  <div className="flex items-center rounded-sm border border-white/15 p-0.5" data-testid="link-lang-switcher">
                    {LINK_LANGS.map((l) => (
                      <button key={l.code} onClick={() => setLinkLang(l.code)} data-testid={`link-lang-${l.code}`}
                        className={`rounded-sm px-2.5 py-1.5 text-[11px] font-semibold transition-colors duration-200 ${
                          linkLang === l.code ? "bg-lc-sky text-lc-navy" : "text-lc-fog hover:text-white"
                        }`}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {!isBulk && (
                    <button onClick={copyLink} disabled={emails.length !== 1} data-testid="link-copy-btn"
                      className="inline-flex items-center justify-center gap-1.5 rounded-sm bg-lc-sky px-4 py-2 text-sm font-semibold text-lc-navy transition-opacity duration-200 hover:opacity-90 disabled:opacity-40">
                      {copied ? <Check size={15} weight="bold" /> : <Copy size={15} weight="bold" />}
                      {copied ? "Copied!" : "Copy Tracking Link"}
                    </button>
                  )}
                  {isBulk && (
                    <>
                      <span className="font-mono text-[11px] uppercase tracking-wider text-lc-sky" data-testid="link-count">{emails.length} links</span>
                      <button onClick={copyAllLinks} data-testid="link-copy-all-btn"
                        className="inline-flex items-center justify-center gap-1.5 rounded-sm bg-lc-sky px-4 py-2 text-sm font-semibold text-lc-navy transition-opacity duration-200 hover:opacity-90">
                        {bulkCopied ? <Check size={15} weight="bold" /> : <Copy size={15} weight="bold" />}
                        {bulkCopied ? "Copied!" : "Copy All Links"}
                      </button>
                      <button onClick={exportLinksCSV} data-testid="link-export-csv-btn"
                        className="inline-flex items-center justify-center gap-1.5 rounded-sm border border-lc-sky/40 bg-lc-sky/10 px-3 py-2 text-sm font-semibold text-lc-sky hover:bg-lc-sky/20 transition-colors duration-200">
                        <FileArrowDown size={15} weight="bold" /> Export CSV
                      </button>
                    </>
                  )}
                </div>

                {emails.length === 1 && (
                  <div className="mt-3 break-all rounded-sm border border-white/10 bg-lc-navy px-3 py-2 font-mono text-[11px] text-lc-sky" data-testid="link-preview">
                    {buildLink(emails[0])}
                  </div>
                )}
                {isBulk && (
                  <div className="mt-3 max-h-40 overflow-y-auto rounded-sm border border-white/10 bg-lc-navy px-3 py-2" data-testid="link-preview-bulk">
                    {emails.map((em) => (
                      <div key={em} className="break-all py-0.5 font-mono text-[11px] text-lc-sky">{buildLink(em)}</div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div className="mt-10 rounded-md border border-white/10 bg-lc-slate/50" data-testid="high-intent-log">
          <div className="flex flex-col gap-3 border-b border-white/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="flex items-center gap-2 font-serif text-lg">
              <Fire size={18} weight="duotone" className="text-lc-sky" /> High-Intent Customer Activity Log
            </h2>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="activity-tabs">
              {ACTIVITY_TABS.map((tb) => {
                const count = activity.filter((r) => matchesTab(r, tb.key)).length;
                return (
                  <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`activity-tab-${tb.key}`}
                    className={`rounded-sm px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-colors duration-200 ${
                      tab === tb.key ? "bg-lc-sky text-lc-navy" : "border border-white/15 text-lc-fog hover:bg-white/5"
                    }`}>
                    {tb.label} <span className={tab === tb.key ? "text-lc-navy/70" : "text-lc-sky"}>{count}</span>
                  </button>
                );
              })}
              <button onClick={exportActivityCSV} data-testid="activity-export-csv"
                className="inline-flex items-center gap-1.5 rounded-sm border border-lc-sky/40 bg-lc-sky/10 px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-wider text-lc-sky hover:bg-lc-sky/20 transition-colors duration-200">
                <FileArrowDown size={13} weight="bold" /> Export CSV
              </button>
            </div>
          </div>
          {(() => {
            const rows = activity.filter((r) => matchesTab(r, tab));
            if (rows.length === 0) {
              return <p className="px-5 py-8 text-center text-sm text-lc-steel" data-testid="activity-empty">No matching activity yet.</p>;
            }
            return (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm" data-testid="activity-table">
                  <thead>
                    <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-lc-steel">
                      <th className="px-5 py-2.5 font-medium">Timestamp</th>
                      <th className="px-5 py-2.5 font-medium">Customer / Identifier</th>
                      <th className="px-5 py-2.5 font-medium">Action</th>
                      <th className="px-5 py-2.5 font-medium">Plan / Status</th>
                      <th className="px-5 py-2.5 font-medium">Intent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {rows.map((r, i) => {
                      const hot = r.is_paid || HOT_EVENTS.has(r.event);
                      const planStatus = r.is_paid
                        ? `Paid${r.plan ? ` · ${r.plan}` : ""}`
                        : (r.plan || "—");
                      return (
                        <tr key={i} className="text-lc-fog hover:bg-white/5 transition-colors duration-150" data-testid={`activity-row-${i}`}>
                          <td className="whitespace-nowrap px-5 py-2.5 font-mono text-[11px] text-lc-steel">{fmtStamp(r.ts)}</td>
                          <td className="px-5 py-2.5">
                            <div className="flex items-center gap-1.5">
                              {r.from_cold_email && <EnvelopeSimple size={13} weight="fill" className="text-lc-sky" title="From cold email link" />}
                              <span className={r.user_email ? "text-white" : "text-lc-steel"}>{r.user_email || `anon · ${(r.session_id || "").slice(0, 8)}`}</span>
                            </div>
                            {r.geo && <span className="mt-0.5 block font-mono text-[10px] text-lc-steel">{r.geo}{r.engine ? ` · ${r.engine}` : ""}</span>}
                          </td>
                          <td className="px-5 py-2.5 text-white">{ACTION_LABEL[r.event] || r.event}</td>
                          <td className="px-5 py-2.5">{planStatus}</td>
                          <td className="px-5 py-2.5">
                            <span className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                              hot ? "bg-lc-critical/15 text-lc-critical" : "bg-lc-sky/15 text-lc-sky"
                            }`} data-testid={`activity-intent-${i}`}>
                              {hot ? "Hot Client" : "Warm Lead"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>

        <div className="mt-10 rounded-md border border-white/10 bg-lc-slate/50" data-testid="recent-activity">
          <div className="border-b border-white/10 px-5 py-3">
            <h2 className="font-serif text-lg">Recent Activity</h2>
          </div>
          {recent.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-lc-steel">No activity recorded yet.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {recent.map((e, i) => {
                const meta = e.meta || {};
                const detail = meta.scenario || meta.mode || meta.format || meta.plan || "";
                return (
                  <div key={i} className="flex items-center justify-between px-5 py-2.5 text-sm" data-testid={`activity-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-lc-sky" />
                      <span className="text-lc-fog">
                        A user {EVENT_LABEL[e.event] || e.event}
                        {detail && <span className="text-white"> · {detail}</span>}
                      </span>
                    </div>
                    <span className="font-mono text-[11px] text-lc-steel">{timeAgo(e.ts)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
